/**
 * Model:
 * key: {
 *  description: Description used in widget selector
 *  property: Name of key in raw event
 *  title1: Title used in widget card in the first line
 *  title2: Title used in widget card in the second line
 *  type: Data type of value in body of widget card
 *  unitLabel: Unit label used in widget card
 *  filterOptions: Filter options used in filter selector
 * }
 */

// Maps widget type to event properties
const WIDGET_TYPE_TO_PROPS_LIST = {
    current_phase_total_time: {
        property: 'currentPhaseTotalTime',
        title1: 'Current Phase',
        title2: 'Total duration',
        type: 'time',
        description: 'Total duration since the beginning of the current phase',
        unitLabel: 'd h m s',
        filterOptions: {
            type: 'Event',
            metric: 'Current phase duration'
        }
    },
    // "Current product count" widgets
    quantity_product_count: {
        property: 'currentProductCount',
        title1: 'Current Product',
        title2: 'Product Count',
        type: 'integer',
        description:
            'Number of products produced since the beginning of the product run.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Product',
            period: 'From the start of the product run'
        }
    },
    current_product_reject: {
        property: 'currentProductReject',
        title1: 'Current Product',
        title2: 'Reject Count',
        type: 'integer',
        description:
            'Number of rejected products since the beginning of the product run.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Product',
            period: 'From the start of the product run'
        }
    },
    quantity_net_product_count: {
        property: 'currentNetProductCount',
        title1: 'Current Product',
        title2: 'Net Product Count',
        type: 'integer',
        description:
            'Net number of products produced since the beginning of the product run.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Product',
            period: 'From the start of the product run'
        }
    },
    quantity_product_unit: {
        property: 'currentProductUnit',
        title1: 'Current Product',
        title2: 'Total Quantity',
        type: 'float',
        description:
            'Quantity of products produced (kg/L/lbs/gal) since the beginning of the product run.',
        unitLabel: 'unit count',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Quantity',
            period: 'From the start of the product run'
        }
    },
    quantity_net_product_unit: {
        property: 'currentNetProductUnit',
        title1: 'Current Product',
        title2: 'Net Quantity',
        type: 'float',
        description:
            'Net quantity of products produced (kg/L/lbs/gal) since the beginning of the product run.',
        unitLabel: 'quantity',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Quantity',
            period: 'From the start of the product run'
        }
    },
    quantity_package_count: {
        property: 'currentPackageCount',
        title1: 'Current Product',
        title2: 'Package Count',
        altTitle1: 'Current Product',
        altTitle2: 'Unit Count',
        type: 'integer',
        description:
            'Number of packages produced since the beginning of the product run.',
        altDescription:
            'Number of units produced since the beginning of the product run.',
        unitLabel: 'packages',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Packages',
            altUnit: 'Units'
        }
    },
    quantity_reject_package_count: {
        property: 'currentRejectPackageCount',
        title1: 'Current Product',
        title2: 'Reject Package Count',
        altTitle1: 'Current Product',
        altTitle2: 'Reject Unit Count',
        type: 'integer',
        description:
            'Number of rejected packages produced since the beginning of the product run.',
        altDescription:
            'Number of rejected units produced since the beginning of the product run.',
        unitLabel: 'packages',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Packages',
            altUnit: 'Units'
        }
    },
    quantity_net_package_count: {
        property: 'currentNetPackageCount',
        title1: 'Current Product',
        title2: 'Net Package Count',
        altTitle1: 'Current Product',
        altTitle2: 'Net Unit Count',
        type: 'integer',
        description:
            'Net number of packages produced since the beginning of the product run.',
        altDescription:
            'Net number of units produced since the beginning of the product run.',
        unitLabel: 'packages',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Packages',
            altUnit: 'Units'
        }
    },
    // "current shift count" widgets
    quantity_shift_count: {
        property: 'currentShiftCount',
        title1: 'Current Shift',
        title2: 'Product Count',
        type: 'integer',
        description:
            'Number of products produced since the beginning of the shift.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Product',
            period: 'From the start of the shift'
        }
    },
    current_shift_reject: {
        property: 'currentShiftReject',
        title1: 'Current Shift',
        title2: 'Reject Count',
        type: 'integer',
        description:
            'Number of rejected products since the beginning of the current shift.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Product',
            period: 'From the start of the shift'
        }
    },
    quantity_net_shift_count: {
        property: 'currentNetShiftCount',
        title1: 'Current Shift',
        title2: 'Net Product Count',
        type: 'integer',
        description:
            'Net number of products produced since the beginning of the shift.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Product',
            period: 'From the start of the shift'
        }
    },
    quantity_shift_net_product_unit: {
        property: 'currentShiftNetCurrentProductUnit',
        title1: 'Current Shift',
        title2: 'Net Quantity',
        type: 'float',
        description:
            'Net quantity of products produced (kg/L/lbs/gal) since the beginning of the shift for the current product.',
        unitLabel: 'quantity',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Quantity',
            period: 'From the start of the shift'
        }
    },
    quantity_shift_product_count: {
        property: 'currentShiftCurrentProductCount',
        title1: 'Current Shift',
        title2: 'Current Product Count',
        type: 'integer',
        description:
            'Number of products produced since the beginning of the shift for the current product.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Product',
            period: 'From the start of the shift'
        }
    },
    current_shift_product_reject: {
        property: 'currentShiftCurrentProductReject',
        title1: 'Current Shift',
        title2: 'Current Product Rejects',
        type: 'integer',
        description:
            'Number of rejected products since the beginning of the current shift for the current product.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Product',
            period: 'From the start of the shift'
        }
    },
    quantity_shift_unit: {
        // This is named incorrectly for backwards compatibility
        // should be 'quantity_shift_product_unit' instead of "quantity_shift_unit"
        property: 'currentShiftCurrentProductUnit',
        title1: 'Current Shift',
        title2: 'Current Product Quantity',
        type: 'float',
        description:
            'Quantity of products produced (kg/L/lbs/gal) since the beginning of the shift for the current product.',
        unitLabel: 'unit count',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Quantity',
            period: 'From the start of the shift'
        }
    },
    // "today/yesterday count" widgets
    total_count_current_day: {
        property: 'totalCountCurrentDay',
        title1: 'Today',
        title2: 'Product Count',
        type: 'integer',
        description: 'Total number of products for today.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Product',
            period: 'From the start of the current day'
        }
    },
    total_count_current_day_reject: {
        property: 'totalCountCurrentDayReject',
        title1: 'Today',
        title2: 'Reject Count',
        type: 'integer',
        description: 'Total number of rejected products for today.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Product',
            period: 'From the start of the current day'
        }
    },
    total_net_count_current_day: {
        property: 'totalNetCountCurrentDay',
        title1: 'Today',
        title2: 'Net Product Count',
        type: 'integer',
        description: 'Total net number of products for today.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Net',
            unit: 'Product',
            period: 'From the start of the current day'
        }
    },
    total_count_yesterday: {
        property: 'totalCountYesterday',
        title1: 'Yesterday',
        title2: 'Product Count',
        type: 'integer',
        description: 'Total number of products for the previous day.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Product',
            period: 'Yesterday'
        }
    },
    total_count_yesterday_reject: {
        property: 'totalCountYesterdayReject',
        title1: 'Yesterday',
        title2: 'Reject Count',
        type: 'integer',
        description: 'Total number of rejected products for the previous day.',
        unitLabel: 'products',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Product',
            period: 'Yesterday'
        }
    },
    // last 1/5/60 minutes quantity
    production_quantity_last_60min_kg: {
        property: 'productionQuantityLast60MinKg',
        title1: 'Production Quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'Quantity of products produced (in kg/lbs) over the last 60 minutes.',
        unitLabel: 'kg',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Quantity',
            period: 'Last 60 min',
            quantityUnit: 'Weight'
        }
    },
    production_quantity_last_60min_litre: {
        property: 'productionQuantityLast60MinLitre',
        title1: 'Production Quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'Quantity of products produced (in L/gal) over the last 60 minutes.',
        unitLabel: 'litre',
        filterOptions: {
            type: 'Production',
            countType: 'Gross',
            unit: 'Quantity',
            period: 'Last 60 min',
            quantityUnit: 'Volume'
        }
    },
    reject_quantity_last_60min_kg: {
        property: 'rejectQuantityLast60MinKg',
        title1: 'Reject Quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'Quantity of products rejected (in kg/lbs) over the last 60 minutes.',
        unitLabel: 'kg',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Quantity',
            period: 'Last 60 min',
            quantityUnit: 'Weight'
        }
    },
    reject_quantity_last_60min_litre: {
        property: 'rejectQuantityLast60MinLitre',
        title1: 'Reject Quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'Quantity of products rejected (in L/gal) over the last 60 minutes.',
        unitLabel: 'litre',
        filterOptions: {
            type: 'Production',
            countType: 'Reject',
            unit: 'Quantity',
            period: 'Last 60 min',
            quantityUnit: 'Volume'
        }
    },
    // downtimes
    downtime_current_duration: {
        property: 'currDowntimeInSec',
        title1: 'Current Product',
        title2: 'Live Downtime',
        type: 'time',
        description: 'Current downtime duration.',
        unitLabel: 'd h m s',
        filterOptions: {
            type: 'Event',
            metric: 'Current downtime duration'
        }
    },
    downtime_product_duration: {
        property: 'productDowntimeInSec',
        title1: 'Current Product',
        title2: 'All Downtime',
        type: 'time',
        description: 'Total downtime duration for current product',
        unitLabel: 'd h m s',
        filterOptions: {
            type: 'Event',
            metric: 'Total downtime duration',
            period: 'From the start of the product run'
        }
    },
    downtime_product_count: {
        property: 'downtimeProductCount',
        title1: 'Current Product',
        title2: 'Downtime Totals',
        type: 'integer',
        description: 'Total downtime count since the beginning of product run.',
        unitLabel: 'units',
        filterOptions: {
            type: 'Event',
            metric: 'Number of downtimes',
            period: 'From the start of the product run'
        }
    },
    downtime_shift_count: {
        property: 'downtimeShiftCount',
        title1: 'Current Shift',
        title2: 'Current Product Downtimes',
        type: 'integer',
        description:
            'Number of downtimes since the beginning of the shift, for the current product',
        unitLabel: 'units',
        filterOptions: {
            type: 'Event',
            metric: 'Number of downtimes',
            period: 'From the start of the shift'
        }
    },
    // Downtime - durations
    downtime_shift_duration: {
        property: 'shiftDowntimeInSec',
        title1: 'Current Shift',
        title2: 'Current Product Downtime',
        type: 'time',
        description:
            'Total downtime duration since the beginning of current shift, for the current product',
        unitLabel: 'd h m s',
        filterOptions: {
            type: 'Event',
            metric: 'Total downtime duration',
            period: 'From the start of the shift'
        }
    },
    // Quality
    quality_current_day: {
        property: 'qualityCurrentDay',
        title1: 'Today',
        title2: 'Quality',
        type: 'float',
        description: 'Quality of the asset for today.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'From the start of the current day'
        }
    },
    quality_yesterday: {
        property: 'qualityYesterday',
        title1: 'Yesterday',
        title2: 'Quality',
        type: 'float',
        description: 'Quality of the asset for the previous day.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'Yesterday'
        }
    },
    quality_current_shift: {
        property: 'qualityCurrentShift',
        title1: 'Current Shift',
        title2: 'Quality',
        type: 'float',
        description: 'Quality of the asset for the current shift.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'From the start of the shift'
        }
    },
    quality_current_product: {
        property: 'qualityCurrentProduct',
        title1: 'Current Product',
        title2: 'Quality',
        type: 'float',
        description: 'Quality of the asset for the current product.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'From the start of the product run'
        }
    },
    quality_1min: {
        property: 'quality1Min',
        title1: 'Quality',
        title2: 'Last 1 min',
        type: 'float',
        description: 'The quality over the last minute.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'Last 1 min'
        }
    },
    quality_5min: {
        property: 'quality5Min',
        title1: 'Quality',
        title2: 'Last 5 min',
        type: 'float',
        description: 'The quality over the last 5 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'Last 5 min'
        }
    },
    quality_60min: {
        property: 'quality60Min',
        title1: 'Quality',
        title2: 'Last 60 min',
        type: 'float',
        description: 'The quality over the last 60 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Quality',
            period: 'Last 60 min'
        }
    },
    // Performance
    performance_current_day: {
        property: 'performanceCurrentDay',
        title1: 'Today',
        title2: 'Performance',
        type: 'float',
        description: 'Performance of the asset for today.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'From the start of the current day'
        }
    },
    performance_yesterday: {
        property: 'performanceYesterday',
        title1: 'Yesterday',
        title2: 'Performance',
        type: 'float',
        description: 'Performance of the asset for the previous day.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'Yesterday'
        }
    },
    performance_current_shift: {
        property: 'performanceCurrentShift',
        title1: 'Current Shift',
        title2: 'Performance',
        type: 'float',
        description: 'Performance of the asset for the current shift.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'From the start of the shift'
        }
    },
    performance_current_product: {
        property: 'performanceCurrentProduct',
        title1: 'Current Product',
        title2: 'Performance',
        type: 'float',
        description: 'Performance of the asset for the current product.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'From the start of the product run'
        }
    },
    performance_1min: {
        property: 'performance1Min',
        title1: 'Performance',
        title2: 'Last 1 min',
        type: 'float',
        description: 'The performance over the last minute.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'Last 1 min'
        }
    },
    performance_5min: {
        property: 'performance5Min',
        title1: 'Performance',
        title2: 'Last 5 min',
        type: 'float',
        description: 'The performance over the last 5 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'Last 5 min'
        }
    },
    performance_60min: {
        property: 'performance60Min',
        title1: 'Performance',
        title2: 'Last 60 min',
        type: 'float',
        description: 'The performance over the last 60 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Performance',
            period: 'Last 60 min'
        }
    },
    // Uptime
    uptime_current_day: {
        property: 'uptimeCurrentDay',
        title1: 'Today',
        title2: 'Uptime',
        type: 'float',
        description: 'Uptime of the asset for today.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'From the start of the current day'
        }
    },
    uptime_yesterday: {
        property: 'uptimeYesterday',
        title1: 'Yesterday',
        title2: 'Uptime',
        type: 'float',
        description: 'Uptime of the asset for the previous day.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'Yesterday'
        }
    },
    uptime_current_shift: {
        property: 'uptimeCurrentShift',
        title1: 'Current Shift',
        title2: 'Uptime',
        type: 'float',
        description: 'Uptime of the asset for the current shift.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'From the start of the shift'
        }
    },
    uptime_current_product: {
        property: 'uptimeCurrentProduct',
        title1: 'Current Product',
        title2: 'Uptime',
        type: 'float',
        description: 'Uptime of the asset for the current product.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'From the start of the product run'
        }
    },
    uptime_1min: {
        property: 'uptime1Min',
        title1: 'Uptime',
        title2: 'Last 1 min',
        type: 'float',
        description: 'The uptime over the last minute.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'Last 1 min'
        }
    },
    uptime_5min: {
        property: 'uptime5Min',
        title1: 'Uptime',
        title2: 'Last 5 min',
        type: 'float',
        description: 'The uptime over the last 5 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'Last 5 min'
        }
    },
    uptime_60min: {
        property: 'uptime60Min',
        title1: 'Uptime',
        title2: 'Last 60 min',
        type: 'float',
        description: 'The uptime over the last 60 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'Uptime',
            period: 'Last 60 min'
        }
    },
    // OEE
    oee_current_day: {
        property: 'oeeCurrentDay',
        title1: 'Today',
        title2: 'OEE',
        type: 'float',
        description: 'OEE of the asset for today.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'From the start of the current day'
        }
    },
    oee_yesterday: {
        property: 'oeeYesterday',
        title1: 'Yesterday',
        title2: 'OEE',
        type: 'float',
        description: 'OEE of the asset for the previous day.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'Yesterday'
        }
    },
    oee_current_shift: {
        property: 'oeeCurrentShift',
        title1: 'Current Shift',
        title2: 'OEE',
        type: 'float',
        description: 'OEE of the asset for the current shift.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'From the start of the shift'
        }
    },
    oee_current_product: {
        property: 'oeeCurrentProduct',
        title1: 'Current Product',
        title2: 'OEE',
        type: 'float',
        description: 'OEE of the asset for the current product.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'From the start of the product run'
        }
    },
    oee_1min: {
        property: 'oee1Min',
        title1: 'OEE',
        title2: 'Last 1 min',
        type: 'float',
        description: 'The OEE over the last minute.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'Last 1 min'
        }
    },
    oee_5min: {
        property: 'oee5Min',
        title1: 'OEE',
        title2: 'Last 5 min',
        type: 'float',
        description: 'The OEE over the last 5 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'Last 5 min'
        }
    },
    oee_60min: {
        property: 'oee60Min',
        title1: 'OEE',
        title2: 'Last 60 min',
        type: 'float',
        description: 'The OEE over the last 60 minutes.',
        unitLabel: '%',
        filterOptions: {
            type: 'KPI',
            metric: 'OEE',
            period: 'Last 60 min'
        }
    },
    // Average speed in units/min
    speed_count_1min: {
        property: 'speedCountLast1Min',
        title1: 'Average speed',
        title2: 'Last 1 min',
        type: 'float',
        description:
            'The average speed over the last minute. Shown as (products/min).',
        unitLabel: 'products/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per minute',
            period: 'Last 1 min'
        }
    },
    speed_count_5min: {
        property: 'speedCountLast5Min',
        title1: 'Average speed',
        title2: 'Last 5 min',
        type: 'float',
        description:
            'The average speed over the last 5 minutes. Shown as (products/min).',
        unitLabel: 'products/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per minute',
            period: 'Last 5 min'
        }
    },
    speed_count_60min: {
        property: 'speedCountLast60Min',
        title1: 'Average speed',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'The average speed over the last 60 minutes. Shown as (products/min).',
        unitLabel: 'products/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per minute',
            period: 'Last 60 min'
        }
    },
    // Average speed in units/hour
    speed_count_1min_per_h: {
        property: 'speedCountLast1MinPerHour',
        title1: 'Average speed',
        title2: 'Last 1 min',
        type: 'float',
        description:
            'The average speed over the last minute. Shown as (products/hour).',
        unitLabel: 'products/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per hour',
            period: 'Last 1 min'
        }
    },
    speed_count_5min_per_h: {
        property: 'speedCountLast5MinPerHour',
        title1: 'Average speed',
        title2: 'Last 5 min',
        type: 'float',
        description:
            'The average speed over the last 5 minutes. Shown as (products/hour).',
        unitLabel: 'products/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per hour',
            period: 'Last 5 min'
        }
    },
    speed_count_60min_per_h: {
        property: 'speedCountLast60MinPerHour',
        title1: 'Average speed',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'The average speed over the last 60 minutes. Shown as (products/hour).',
        unitLabel: 'products/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Products',
            timeUnit: 'Per hour',
            period: 'Last 60 min'
        }
    },
    // Average speed in quantity/min (ex: litre/min)
    speed_quantity_1min: {
        property: 'speedQuantityLast1Min',
        title1: 'Average speed quantity',
        title2: 'Last 1 min',
        type: 'float',
        description:
            'The average product quantity over the last minute. Shown as (quantity/min).',
        unitLabel: 'quantity/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per minute',
            period: 'Last 1 min'
        }
    },
    speed_quantity_5min: {
        property: 'speedQuantityLast5Min',
        title1: 'Average speed quantity',
        title2: 'Last 5 min',
        type: 'float',
        description:
            'The average product quantity over the last 5 minutes. Shown as (quantity/min).',
        unitLabel: 'quantity/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per minute',
            period: 'Last 5 min'
        }
    },
    speed_quantity_60min: {
        property: 'speedQuantityLast60Min',
        title1: 'Average speed quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'The average product quantity over the last 60 minutes. Shown as (quantity/min).',
        unitLabel: 'quantity/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per minute',
            period: 'Last 60 min'
        }
    },
    // Average speed in quantity/hour (ex: litre/hour)
    speed_quantity_1min_per_h: {
        property: 'speedQuantityLast1MinPerHour',
        title1: 'Average speed quantity',
        title2: 'Last 1 min',
        type: 'float',
        description:
            'The average product quantity over the last minute. Shown as (quantity/hour).',
        unitLabel: 'quantity/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per hour',
            period: 'Last 1 min'
        }
    },
    speed_quantity_5min_per_h: {
        property: 'speedQuantityLast5MinPerHour',
        title1: 'Average speed quantity',
        title2: 'Last 5 min',
        type: 'float',
        description:
            'The average product quantity over the last 5 minutes. Shown as (quantity/hour).',
        unitLabel: 'quantity/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per hour',
            period: 'Last 5 min'
        }
    },
    speed_quantity_60min_per_h: {
        property: 'speedQuantityLast60MinPerHour',
        title1: 'Average speed quantity',
        title2: 'Last 60 min',
        type: 'float',
        description:
            'The average product quantity over the last 60 minutes. Shown as (quantity/hour).',
        unitLabel: 'quantity/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Quantity',
            timeUnit: 'Per hour',
            period: 'Last 60 min'
        }
    }
};

const PACKAGE_SPEED_WIDGETS_TO_PROPS_LIST = {
    // Average speed in packages/min
    package_speed_1min_per_min: {
        property: 'packageSpeedLast1Min',
        title1: 'Average package speed',
        title2: 'Last 1 min',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 1 min',
        type: 'float',
        description:
            'The average speed over the last minute. Shown as (packages/min).',
        altDescription:
            'The average speed over the last minute. Shown as (units/min).',
        unitLabel: 'packages/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per minute',
            period: 'Last 1 min'
        }
    },
    package_speed_5min_per_min: {
        property: 'packageSpeedLast5Min',
        title1: 'Average package speed',
        title2: 'Last 5 min',
        type: 'float',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 5 min',
        description:
            'The average speed over the last 5 minutes. Shown as (packages/min).',
        altDescription:
            'The average speed over the last 5 minutes. Shown as (units/min).',
        unitLabel: 'packages/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per minute',
            period: 'Last 5 min'
        }
    },
    package_speed_60min_per_min: {
        property: 'packageSpeedLast60Min',
        title1: 'Average package speed',
        title2: 'Last 60 min',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 60 min',
        type: 'float',
        description:
            'The average speed over the last 60 minutes. Shown as (packages/min).',
        altDescription:
            'The average speed over the last 60 minutes. Shown as (units/min).',
        unitLabel: 'packages/min',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per minute',
            period: 'Last 60 min'
        }
    },
    // Average speed in packages/hour
    package_speed_1min_per_h: {
        property: 'packageSpeedLast1MinPerHour',
        title1: 'Average package speed',
        title2: 'Last 1 min',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 1 min',
        type: 'float',
        description:
            'The average speed over the last minute. Shown as (packages/hour).',
        altDescription:
            'The average speed over the last minute. Shown as (units/hour).',
        unitLabel: 'packages/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per hour',
            period: 'Last 1 min'
        }
    },
    package_speed_5min_per_h: {
        property: 'packageSpeedLast5MinPerHour',
        title1: 'Average package speed',
        title2: 'Last 5 min',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 5 min',
        type: 'float',
        description:
            'The average speed over the last 5 minutes. Shown as (packages/hour).',
        altDescription:
            'The average speed over the last 5 minutes. Shown as (units/hour).',
        unitLabel: 'packages/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per hour',
            period: 'Last 5 min'
        }
    },
    package_speed_60min_per_h: {
        property: 'packageSpeedLast60MinPerHour',
        title1: 'Average package speed',
        title2: 'Last 60 min',
        altTitle1: 'Average unit speed',
        altTitle2: 'Last 60 min',
        type: 'float',
        description:
            'The average speed over the last 60 minutes. Shown as (packages/hour).',
        altDescription:
            'The average speed over the last 60 minutes. Shown as (units/hour).',
        unitLabel: 'packages/hour',
        filterOptions: {
            type: 'Speed',
            unit: 'Packages',
            altUnit: 'Units',
            timeUnit: 'Per hour',
            period: 'Last 60 min'
        }
    }
};

function widgetsList() {
    return {
        ...WIDGET_TYPE_TO_PROPS_LIST,
        ...PACKAGE_SPEED_WIDGETS_TO_PROPS_LIST
    };
}

const WIDGET_TYPE_TO_PROPS = widgetsList();

const CALCULATED_WIDGETS = [
    'qualityToday',
    'qualityYesterday',
    'qualityCurrentShift',
    'qualityCurrentProduct',
    'oeeToday',
    'oeeYesterday',
    'oeeCurrentShift',
    'oeeCurrentProduct',
    'oee1Min',
    'oee5Min',
    'oee60Min'
];
export { CALCULATED_WIDGETS, WIDGET_TYPE_TO_PROPS };
